import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Card,
  TextField,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  LinearProgress,
  Checkbox,
  Divider,
  FormHelperText
} from '@material-ui/core';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import Autocomplete from '@material-ui/lab/Autocomplete';


const RequestPromotionPercentageOff = ({
  values,
  setFieldValue,
  touched,
  errors
}) => {
  const { storeActiveCategoriesWithAllActiveParents, loadingCategories } = useSelector((state: IReduxState) => state.categories);
  const { store_products: { catalogActiveProducts }, productsLoading } = useSelector((state: IReduxState) => state.products);


  const renderOption = (option, { inputValue, type }) => {
    const optionText = type === 'categories' ? option?.nameWithPath : option.name;
    const matches = match(optionText, inputValue);
    const parts = parse(optionText, matches);

    return (
      <div>
        {parts.map((part, index) => (
          <span key={index} className={part.highlight ? 'text-primary font-bold' : ''}>
            {part.text}
          </span>
        ))}
        {type === 'listings' && Boolean(option.price) && (
          <span className="ml-2 text-green">${option.price}</span>
        )}
      </div>
    );
  };

  return (
    <Card className="p-5 mt-4" elevation={0}>
      <Typography variant="h5" className="font-bold mb-5">
        Applies To <span className="text-error">*</span>
      </Typography>
      <FormControl component="fieldset" className="w-full">
        <RadioGroup
          aria-label="applies to"
          name="percentageOff.appliesTo"
          value={values.percentageOff.appliesTo.name}
          onChange={(event) => {
            setFieldValue('percentageOff.appliesTo.name', event.target.value);
          }}
        >
          <FormControlLabel
            value="Storewide"
            control={<Radio size="small" color="primary" />}
            label="Storewide"
          />
          <FormControlLabel
            value="All Products"
            control={<Radio size="small" color="primary" />}
            label="All Products"
          />
          <FormControlLabel
            value="All Treatments"
            control={<Radio size="small" color="primary" />}
            label="All Treatments"
          />
          <FormControlLabel
            value="Specific Categories/Listings"
            control={
              <Radio
                size="small"
                color="primary"
              />
            }
            label="Specific Categories/Listings"
          />
          {values.percentageOff.appliesTo.name === 'Specific Categories/Listings' &&
          <div className="ml-7 flex-column">
            <FormControlLabel
              name="Categories"
              control={
                <Checkbox
                  size="small"
                  color="primary"
                  checked={values.percentageOff.appliesTo.isCategories}
                  onChange={(e) => setFieldValue('percentageOff.appliesTo.isCategories', e.target.checked)}
                />
              }
              label="Categories"
            />
            {values.percentageOff.appliesTo.isCategories &&
            <>
              {loadingCategories ?
                <div className="my-5">
                  <span className="text-muted">Preparing Categories Data...</span>
                  <LinearProgress />
                </div>
                :
                <Autocomplete
                  multiple
                  fullWidth
                  disableClearable
                  size="small"
                  className="my-5"
                  value={values.percentageOff.appliesTo.value.categories}
                  options={storeActiveCategoriesWithAllActiveParents}
                  getOptionLabel={(option) => option.nameWithPath}
                  onChange={(e, value) => {
                    setFieldValue(
                      'percentageOff.appliesTo.value.categories',
                      value !== null ? value : values.percentageOff.appliesTo.value.categories
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="percentageOff.appliesTo.value.categories"
                      variant="outlined"
                      label="Categories"
                      required
                      error={touched.percentageOff?.appliesTo?.value?.categories && Boolean(errors.percentageOff?.appliesTo?.value?.categories)}
                      helperText={touched.percentageOff?.appliesTo?.value?.categories && errors.percentageOff?.appliesTo?.value?.categories}
                    />
                  )}
                  renderOption={(option, { inputValue }) => renderOption(option, { inputValue, type: 'categories' })}
                />
              }
            </>
            }
            <FormControlLabel
              name="Listings"
              control={
                <Checkbox
                  size="small"
                  color="primary"
                  checked={values.percentageOff.appliesTo.isListings}
                  onChange={(e) => setFieldValue('percentageOff.appliesTo.isListings', e.target.checked)}
                />
              }
              label="Listings"
            />
            {values.percentageOff.appliesTo.isListings &&
            <>
              {productsLoading ?
                <div className="my-5">
                  <span className="text-muted">Preparing Listings Data...</span>
                  <LinearProgress />
                </div>
                :
                <Autocomplete
                  multiple
                  fullWidth
                  disableClearable
                  noOptionsText={
                    <Link
                      to="/request-sku" target="_blank" onMouseDown={() => {
                        window.open('/request-sku', '_blank');
                      }}
                    >
                      Request Listings
                    </Link>
                  }
                  size="small"
                  className="my-5"
                  value={values.percentageOff.appliesTo.value.listings}
                  options={catalogActiveProducts}
                  getOptionLabel={(option) => option.name}
                  onChange={(e, value) => {
                    setFieldValue(
                      'percentageOff.appliesTo.value.listings',
                      value !== null ? value : values.percentageOff.appliesTo.value.listings
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="percentageOff.appliesTo.value.listings"
                      variant="outlined"
                      label="Listings"
                      required
                      error={touched.percentageOff?.appliesTo?.value?.listings && Boolean(errors.percentageOff?.appliesTo?.value?.listings)}
                      helperText={touched.percentageOff?.appliesTo?.value?.listings && errors.percentageOff?.appliesTo?.value?.listings}
                    />
                  )}
                  renderOption={(option, { inputValue }) => renderOption(option, { inputValue, type: 'listings' })}
                />
              }
            </>
            }
            {(touched.percentageOff?.appliesTo?.isCategories ||
              touched.percentageOff?.appliesTo?.isListings) &&
              (Boolean(errors.percentageOff?.appliesTo?.isCategories) ||
              Boolean(errors.percentageOff?.appliesTo?.isListings)) &&
              <FormHelperText>
                <Typography color="error" variant="caption">
                  At least one needs to be selected
                </Typography>
              </FormHelperText>
            }
          </div>
          }
          {touched.percentageOff?.appliesTo?.name && Boolean(errors.percentageOff?.appliesTo?.name) &&
          <FormHelperText>
            <Typography color="error" variant="caption">
              {errors.percentageOff.appliesTo.name}
            </Typography>
          </FormHelperText>
          }
        </RadioGroup>
      </FormControl>
      <Divider className="my-5" />
      <Typography variant="h5" className="font-bold mb-5">
        Does Not Apply To
      </Typography>
      <FormControl component="fieldset" className="w-full">
        <RadioGroup
          aria-label="not applies to"
          name="percentageOff.notAppliesTo"
          value={values.percentageOff.notAppliesTo.name}
          onChange={(event) => {
            setFieldValue('percentageOff.notAppliesTo.name', event.target.value);
          }}
        >
          <FormControlLabel
            value="None"
            control={<Radio size="small" color="primary" />}
            label="None"
          />
          <FormControlLabel
            value="All Products"
            control={<Radio size="small" color="primary" />}
            label="All Products"
          />
          <FormControlLabel
            value="All Treatments"
            control={<Radio size="small" color="primary" />}
            label="All Treatments"
          />
          <FormControlLabel
            value="Specific Categories/Listings"
            control={
              <Radio
                size="small"
                color="primary"
              />
            }
            label="Specific Categories/Listings"
          />
          {values.percentageOff.notAppliesTo.name === 'Specific Categories/Listings' &&
          <div className="ml-7 flex-column">
            <FormControlLabel
              name="Categories"
              control={
                <Checkbox
                  size="small"
                  color="primary"
                  checked={values.percentageOff.notAppliesTo.isCategories}
                  onChange={(e) => setFieldValue('percentageOff.notAppliesTo.isCategories', e.target.checked)}
                />
              }
              label="Categories"
            />
            {values.percentageOff.notAppliesTo.isCategories &&
            <>
              {loadingCategories ?
                <div className="my-5">
                  <span className="text-muted">Preparing Categories Data...</span>
                  <LinearProgress />
                </div>
                :
                <Autocomplete
                  multiple
                  fullWidth
                  disableClearable
                  size="small"
                  className="my-5"
                  value={values.percentageOff.notAppliesTo.value.categories}
                  options={storeActiveCategoriesWithAllActiveParents}
                  getOptionLabel={(option) => option.nameWithPath}
                  onChange={(e, value) => {
                    setFieldValue(
                      'percentageOff.notAppliesTo.value.categories',
                      value !== null ? value : values.percentageOff.notAppliesTo.value.categories
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="percentageOff.notAppliesTo.value.categories"
                      variant="outlined"
                      label="Categories"
                      required
                      error={touched.percentageOff?.notAppliesTo?.value?.categories && Boolean(errors.percentageOff?.notAppliesTo?.value?.categories)}
                      helperText={touched.percentageOff?.notAppliesTo?.value?.categories && errors.percentageOff?.notAppliesTo?.value?.categories}
                    />
                  )}
                  renderOption={(option, { inputValue }) => renderOption(option, { inputValue, type: 'categories' })}
                />
              }
            </>
            }
            <FormControlLabel
              name="Listings"
              control={
                <Checkbox
                  size="small"
                  color="primary"
                  checked={values.percentageOff.notAppliesTo.isListings}
                  onChange={(e) => setFieldValue('percentageOff.notAppliesTo.isListings', e.target.checked)}
                />
              }
              label="Listings"
            />
            {values.percentageOff.notAppliesTo.isListings &&
            <>
              {productsLoading ?
                <div className="my-5">
                  <span className="text-muted">Preparing Listings Data...</span>
                  <LinearProgress />
                </div>
                :
                <Autocomplete
                  multiple
                  fullWidth
                  disableClearable
                  noOptionsText={
                    <Link
                      to="/request-sku" target="_blank" onMouseDown={() => {
                        window.open('/request-sku', '_blank');
                      }}
                    >
                      Request Listings
                    </Link>
                  }
                  size="small"
                  className="my-5"
                  value={values.percentageOff.notAppliesTo.value.listings}
                  options={catalogActiveProducts}
                  getOptionLabel={(option) => option.name}
                  onChange={(e, value) => {
                    setFieldValue(
                      'percentageOff.notAppliesTo.value.listings',
                      value !== null ? value : values.percentageOff.notAppliesTo.value.listings
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="percentageOff.notAppliesTo.value.listings"
                      variant="outlined"
                      label="Listings"
                      required
                      error={touched.percentageOff?.notAppliesTo?.value?.listings && Boolean(errors.percentageOff?.notAppliesTo?.value?.listings)}
                      helperText={touched.percentageOff?.notAppliesTo?.value?.listings && errors.percentageOff?.notAppliesTo?.value?.listings}
                    />
                  )}
                  renderOption={(option, { inputValue }) => renderOption(option, { inputValue, type: 'listings' })}
                />
              }
            </>
            }
          </div>
          }
          {(touched.percentageOff?.notAppliesTo?.isCategories ||
            touched.percentageOff?.notAppliesTo?.isListings) &&
            (Boolean(errors.percentageOff?.notAppliesTo?.isCategories) ||
            Boolean(errors.percentageOff?.notAppliesTo?.isListings)) &&
            values.percentageOff.notAppliesTo.name === 'Specific Categories/Listings' &&
            <FormHelperText>
              <Typography color="error" variant="caption">
                At least one needs to be selected
              </Typography>
            </FormHelperText>
          }
          {touched.percentageOff?.notAppliesTo?.name && Boolean(errors.percentageOff?.notAppliesTo?.name) &&
          <FormHelperText>
            <Typography color="error" variant="caption">
              {errors.percentageOff.notAppliesTo.name}
            </Typography>
          </FormHelperText>
          }
        </RadioGroup>
      </FormControl>
    </Card>
  );
};

export default RequestPromotionPercentageOff;
